<script module lang="ts">
  import type { FeedCell } from './alerts.svelte.ts';

  /** A region's cells, kept for the next time the view opens: the score moves every ten minutes. */
  const FEED_TTL_MS = 5 * 60 * 1000;
  const regions = new Map<string, { cells: FeedCell[]; at: number }>();
  /** The key the hotspot list is kept under beside the regions; no cell index looks like it. */
  const EVERYWHERE = '*';
</script>

<script lang="ts">
  /**
   * Umschau — the camera view (amendment 18).
   *
   * Hold the phone up and the cells around you stand in the camera's picture as columns of
   * light, where they really are, with their GDI and how far it is to walk into them. The
   * nearest gets a card of its own at the bottom, and an arrow that turns you towards it.
   *
   * Everything here happens on the phone. The picture goes from the camera to the screen and
   * nowhere else — never uploaded, recorded or kept. The cells come from the region feed the
   * geofence already asks for (brief §10): the res-5 region you are in and the six round it,
   * so the server learns nothing it did not know. Beyond those, the public hotspot list — asked
   * for without any position — names the nearest cell however far it is, because a view that
   * says "nothing here" to somebody twenty kilometres from the action has said nothing. The
   * position comes from a precise watch of its own, while the view is open and not a moment
   * longer — a hundred metres of GPS error is the difference between "here" and "the next
   * street" — and until its first fix from the rougher one the geofence already holds.
   *
   * The drawing is plain DOM moved by transforms once a frame, outside Svelte: a dozen
   * elements whose places change sixty times a second have nothing to gain from reactivity.
   */
  import type { Attribute } from '$domain/attributes.ts';
  import { cellFor, centreOf, neighbours, regionOf } from '$domain/h3.ts';
  import {
    type Basis,
    bearingTo,
    blend,
    cameraHeading,
    compassPoint,
    directionOf,
    distanceBetween,
    elevationOf,
    focalLength,
    type LatLon,
    metresToCell,
    project,
    turnBetween,
  } from '$domain/viewfinder.ts';
  import { onMount } from 'svelte';
  import { alerts, hotCellsIn } from './alerts.svelte.ts';
  import { request } from './api.ts';
  import { columnMetres } from './cell-motion.ts';
  import { gdiColour, gdiLabel, vibeSymbol } from './gdi-scale.ts';
  import { watchCompass } from './orientation.ts';
  import { cachedPlace, reverseGeocode } from './place.ts';
  import { describeAge } from './store.ts';
  import type { Hotspot } from './types.ts';

  type Props = { attributes: Attribute[]; onclose: () => void };
  const { attributes, onclose }: Props = $props();

  /** Cells further than this are left out: past it a column is a hair on the horizon. */
  const RANGE_METRES = 3000;
  /** At most this many columns. More than a dozen cards is a wall, not a view. */
  const MOST = 12;
  /** How quickly the picture follows the compass, as a time constant in seconds. */
  const SMOOTHING_S = 0.12;
  /** A column stays between these lengths on screen: in pixels, and in screen heights. */
  const SHORTEST_BEAM = 34;
  const LONGEST_BEAM = 0.42;
  /** How far past the edge of the picture a column's foot may be and the column still drawn. */
  const EDGE = 40;
  /** The compass tape: pixels per degree, and a tick every 15°. */
  const TAPE_PX = 3;
  const TICKS = Array.from({ length: 72 }, (_, index) => -360 + index * 15);
  const DEGREES = 180 / Math.PI;

  type Target = FeedCell & { bearing: number; edge: number; away: number; height: number };
  type Parts = {
    node: HTMLElement;
    foot: HTMLElement;
    beam: HTMLElement;
    tag: HTMLElement;
    size?: { width: number; height: number };
  };
  type Rect = { left: number; right: number; top: number; bottom: number };

  let root: HTMLDivElement | undefined = $state();
  let video: HTMLVideoElement | undefined = $state();
  let strip: HTMLDivElement | undefined = $state();
  let heading: HTMLSpanElement | undefined = $state();
  let arrow: HTMLSpanElement | undefined = $state();
  let leftCount: HTMLSpanElement | undefined = $state();
  let rightCount: HTMLSpanElement | undefined = $state();

  let position = $state<(LatLon & { accuracy: number }) | undefined>();
  let located = $state<'waiting' | 'live' | 'refused'>('waiting');
  let camera = $state<'starting' | 'live' | 'refused' | 'missing'>('starting');
  let compass = $state<'waiting' | 'live' | 'missing'>('waiting');
  let feed = $state.raw<FeedCell[]>([]);
  let loaded = $state(false);
  let offline = $state(false);
  let portrait = $state(true);
  let place = $state<string | undefined>();
  /**
   * Which edge the nearest cell is beyond, while it is out of the picture. Set from the frame
   * loop, but only when it changes — which is when the cell crosses the edge of the picture.
   */
  let pointerSide = $state<'' | 'left' | 'right'>('');

  /**
   * Every cell near enough to matter, nearest edge first — and the two nearest beyond that,
   * so that there is always a nearest cell to point at, even from inside a cell far from the
   * rest.
   */
  const targets = $derived.by<Target[]>(() => {
    const here = position;
    if (here === undefined) return [];
    const byDistance = feed
      .map((entry) => ({ entry, away: distanceBetween(here, entry) }))
      .sort((a, b) => a.away - b.away);
    const within = byDistance.filter(({ away }) => away <= RANGE_METRES + 500);
    const beyond = byDistance.filter(({ away }) => away > RANGE_METRES + 500).slice(0, 2);
    return [...within, ...beyond]
      .map(({ entry, away }) => ({
        ...entry,
        away,
        bearing: bearingTo(here, entry),
        edge: metresToCell(here, entry.cell),
        height: columnMetres(entry.cell, entry.gdi),
      }))
      .sort((a, b) => a.edge - b.edge);
  });
  /** The cell you are standing in: all round you, so it gets a glow rather than a column. */
  const inside = $derived(targets.find((entry) => entry.edge === 0));
  const nearest = $derived(targets.find((entry) => entry.edge > 0));
  /**
   * The columns in the picture: the cells within range, and the nearest one however far it is.
   * Saying which way to go is the point of the view, and past the range that one column, low on
   * the horizon, is the only thing that does.
   */
  const shown = $derived.by(() => {
    const near = targets.filter((entry) => entry.edge > 0 && entry.edge <= RANGE_METRES);
    const columns = near.slice(0, MOST);
    if (nearest !== undefined && !columns.includes(nearest)) columns.push(nearest);
    return columns;
  });
  const focus = $derived(inside ?? nearest);
  const details = $derived(
    focus === undefined
      ? ''
      : [
          `${focus.sightingCount} Meldungen`,
          focus.lastSightingAt === null ? undefined : `zuletzt ${describeAge(focus.lastSightingAt)}`,
          attributeOf(focus.topAttribute),
        ]
          .filter((part) => part !== undefined)
          .join(' · '),
  );

  const fixText = $derived(
    located === 'refused'
      ? 'Kein Standort'
      : position === undefined
        ? 'Standort …'
        : Number.isFinite(position.accuracy)
          ? `± ${Math.max(1, Math.round(position.accuracy))} m`
          : 'Standort grob',
  );

  function distance(metres: number): string {
    if (metres < 1000) return `${Math.max(10, Math.round(metres / 10) * 10)} m`;
    return `${(metres / 1000).toLocaleString('de-DE', { maximumFractionDigits: 1 })} km`;
  }

  function attributeOf(key: string | null): string | undefined {
    const attribute = attributes.find((entry) => entry.key === key);
    return attribute === undefined ? undefined : `${attribute.emoji} ${attribute.label}`;
  }

  // ---- the cells: the geofence's region feed, and the hotspot list for anything beyond ----

  let region: string | undefined;

  $effect(() => {
    const here = position;
    if (here === undefined) return;
    const now = regionOf(cellFor(here.lat, here.lon));
    if (now === region) return;
    region = now;
    void load(now);
  });

  async function regionFeed(key: string): Promise<FeedCell[]> {
    const known = regions.get(key);
    if (known !== undefined && Date.now() - known.at < FEED_TTL_MS) return known.cells;
    const cells = await hotCellsIn(key);
    regions.set(key, { cells, at: Date.now() });
    return cells;
  }

  /**
   * Every visible cell anywhere, strongest first, from the list the map's Hotspots panel shows.
   * Asked for with no position at all, so it tells the server nothing; it is what finds the
   * nearest cell for somebody standing far from all of them.
   */
  async function everywhere(): Promise<FeedCell[]> {
    const known = regions.get(EVERYWHERE);
    if (known !== undefined && Date.now() - known.at < FEED_TTL_MS) return known.cells;
    const { hotspots } = await request<{ hotspots: Hotspot[] }>('/api/hotspots?limit=100');
    const cells = hotspots.map((spot) => ({
      ...centreOf(spot.cell),
      cell: spot.cell,
      gdi: spot.gdi,
      vibe: spot.vibe,
      vibeVotes: spot.vibeVotes,
      sightingCount: spot.sightingCount,
      topAttribute: spot.topAttribute,
      lastSightingAt: spot.lastSightingAt === null ? null : Date.parse(spot.lastSightingAt),
      facets: {},
      allMine: false,
      judgedAt: null,
    }));
    regions.set(EVERYWHERE, { cells, at: Date.now() });
    return cells;
  }

  async function load(centre: string) {
    const nearby = neighbours(centre, 1).map(regionFeed);
    const results = await Promise.allSettled([...nearby, everywhere()]);
    if (centre !== region) return;
    // The region feed first: for a cell it covers it is the complete answer, and the list
    // only adds what lies further out.
    const cells = new Map<string, FeedCell>();
    for (const result of results) {
      if (result.status !== 'fulfilled') continue;
      for (const cell of result.value) if (!cells.has(cell.cell)) cells.set(cell.cell, cell);
    }
    offline = results.every((result) => result.status === 'rejected');
    feed = [...cells.values()];
    loaded = true;
  }

  // The place name for the card, looked up for that one cell and cached (brief §10).
  $effect(() => {
    const cell = focus?.cell;
    if (cell === undefined) {
      place = undefined;
      return;
    }
    place = cachedPlace(cell);
    if (place !== undefined) return;
    let cancelled = false;
    void reverseGeocode(cell).then((name) => {
      if (!cancelled) place = name;
    });
    return () => {
      cancelled = true;
    };
  });

  // ---- the camera ----

  let stream: MediaStream | undefined;
  let opening = false;
  let closed = false;

  async function startCamera() {
    if (stream !== undefined || opening || closed) return;
    if (navigator.mediaDevices?.getUserMedia === undefined) {
      camera = 'missing';
      return;
    }
    opening = true;
    try {
      const opened = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      if (closed || document.visibilityState !== 'visible') {
        for (const track of opened.getTracks()) track.stop();
        return;
      }
      stream = opened;
      if (video !== undefined) {
        video.srcObject = opened;
        await video.play().catch(() => undefined);
      }
      camera = 'live';
    } catch (cause) {
      const name = cause instanceof DOMException ? cause.name : '';
      camera = name === 'NotAllowedError' || name === 'SecurityError' ? 'refused' : 'missing';
    } finally {
      opening = false;
    }
  }

  /** The camera goes off whenever the view does not show: closed, or the app in the background. */
  function stopCamera() {
    for (const track of stream?.getTracks() ?? []) track.stop();
    stream = undefined;
    if (video !== undefined) video.srcObject = null;
  }

  let lock: WakeLockSentinel | undefined;

  /** Walking with the view open is the point of it: the screen should not go dark on the way. */
  async function keepAwake() {
    try {
      lock = await navigator.wakeLock?.request('screen');
    } catch {
      lock = undefined;
    }
  }

  // ---- the drawing ----

  const parts = new Map<string, Parts>();

  /** Registers a target's three moving parts, so the frame loop can reach them without a query. */
  function track(node: HTMLElement, cell: string) {
    const foot = node.querySelector<HTMLElement>('.ground');
    const beam = node.querySelector<HTMLElement>('.beam');
    const tag = node.querySelector<HTMLElement>('.tag');
    if (foot !== null && beam !== null && tag !== null) parts.set(cell, { node, foot, beam, tag });
    return {
      destroy() {
        parts.delete(cell);
      },
    };
  }

  // A card's size is read once per change of the list, not per frame: reading layout in a frame
  // that writes transforms is how a smooth view becomes a stuttering one.
  $effect(() => {
    void shown;
    for (const part of parts.values()) part.size = undefined;
  });

  let width = 0;
  let height = 0;
  let focal = 1;

  function measure() {
    if (root === undefined) return;
    width = root.clientWidth;
    height = root.clientHeight;
    portrait = height >= width;
    // The picture is scaled to cover the screen; its long side as drawn sets the focal length.
    const videoWidth = video?.videoWidth ?? 0;
    const videoHeight = video?.videoHeight ?? 0;
    const scale =
      videoWidth > 0 && videoHeight > 0
        ? Math.max(width / videoWidth, height / videoHeight)
        : 0;
    focal = focalLength(
      scale > 0 ? Math.max(videoWidth, videoHeight) * scale : Math.max(width, height),
    );
  }

  let latest: Basis | undefined;
  let attitude: Basis | undefined;
  let frame: number | undefined;
  let last = 0;
  let shownHeading = -1;
  let sides = '';

  function draw(now: number) {
    frame = requestAnimationFrame(draw);
    const seconds = last === 0 ? 1 / 60 : Math.min(0.1, (now - last) / 1000);
    last = now;
    if (latest === undefined) return;
    attitude =
      attitude === undefined ? latest : blend(attitude, latest, 1 - Math.exp(-seconds / SMOOTHING_S));
    const look = cameraHeading(attitude);

    if (strip !== undefined) {
      strip.style.transform = `translate3d(${(width / 2 - (look + 360) * TAPE_PX).toFixed(1)}px, 0, 0)`;
    }
    const rounded = Math.round(look) % 360;
    if (heading !== undefined && rounded !== shownHeading) {
      shownHeading = rounded;
      heading.textContent = `${compassPoint(look)} ${rounded}°`;
    }

    let left = 0;
    let right = 0;
    const taken: Rect[] = [];
    for (const target of shown) {
      const part = parts.get(target.cell);
      if (part === undefined) continue;
      const foot = project(
        attitude,
        directionOf(target.bearing, elevationOf(0, target.away)),
        focal,
      );
      const crown = project(
        attitude,
        directionOf(target.bearing, elevationOf(target.height, target.away)),
        focal,
      );
      if (foot === undefined || crown === undefined || Math.abs(foot.x) > width / 2 + EDGE) {
        part.node.style.visibility = 'hidden';
        if (turnBetween(look, target.bearing) < 0) left += 1;
        else right += 1;
        continue;
      }
      part.node.style.visibility = '';
      const x = width / 2 + foot.x;
      const y = height / 2 + foot.y;

      // The column as tall as the map's 3D one would be, from here — but never so short it
      // vanishes on the horizon nor so tall it walls off the screen.
      const dx = crown.x - foot.x;
      const dy = crown.y - foot.y;
      const length = Math.hypot(dx, dy);
      const drawn = Math.min(LONGEST_BEAM * height, Math.max(SHORTEST_BEAM, length));
      const ux = length > 0.5 ? dx / length : 0;
      const uy = length > 0.5 ? dy / length : -1;
      const angle = Math.atan2(ux, -uy) * DEGREES;
      const ring = Math.min(1.5, Math.max(0.25, (focal * 25) / target.away / 45));
      part.foot.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0) scale(${ring.toFixed(3)}) translate(-50%, -50%)`;
      part.beam.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0) rotate(${angle.toFixed(2)}deg) scaleY(${(drawn / 100).toFixed(3)}) translate(-50%, -100%)`;

      // The card on top of the column, pushed up past the cards of nearer cells — and kept on
      // the screen, so a column at the edge still has a card that can be read.
      part.size ??= { width: part.tag.offsetWidth, height: part.tag.offsetHeight };
      // Smaller the further away — except the nearest, which is what somebody is looking for.
      const shrunk = Math.min(1.12, Math.max(0.72, 1.15 - target.away / 4000));
      const scale = target === nearest ? Math.max(1, shrunk) : shrunk;
      const w = part.size.width * scale;
      const h = part.size.height * scale;
      const cx = Math.min(width - w / 2 - 6, Math.max(w / 2 + 6, x + ux * drawn));
      let bottom = y + uy * drawn - 6;
      for (let tries = 0; tries < MOST; tries += 1) {
        const clash = taken.find(
          (rect) =>
            cx - w / 2 < rect.right &&
            cx + w / 2 > rect.left &&
            bottom - h < rect.bottom &&
            bottom > rect.top,
        );
        if (clash === undefined) break;
        bottom = clash.top - 4;
      }
      taken.push({ left: cx - w / 2, right: cx + w / 2, top: bottom - h, bottom });
      part.tag.style.transform = `translate3d(${(cx - w / 2).toFixed(1)}px, ${(bottom - h).toFixed(1)}px, 0) scale(${scale.toFixed(3)})`;
    }

    const counted = `${left}|${right}`;
    if (counted !== sides) {
      sides = counted;
      if (leftCount !== undefined) leftCount.textContent = left > 0 ? `‹ ${left}` : '';
      if (rightCount !== undefined) rightCount.textContent = right > 0 ? `${right} ›` : '';
    }

    // Which way to the nearest cell: the card's arrow always, and a pointer at the edge of the
    // screen while the cell is out of the picture.
    const goal = nearest;
    if (goal === undefined) return;
    const turn = turnBetween(look, goal.bearing);
    if (arrow !== undefined) arrow.style.transform = `rotate(${turn.toFixed(1)}deg)`;
    // The same test the column is drawn by, so the pointer and the column's card never show
    // at once — the card is kept on the screen, and a pointer beside it would cover it.
    const foot = project(attitude, directionOf(goal.bearing, elevationOf(0, goal.away)), focal);
    const inView = foot !== undefined && Math.abs(foot.x) <= width / 2 + EDGE;
    const side = inView ? '' : turn < 0 ? 'left' : 'right';
    if (side !== pointerSide) pointerSide = side;
  }

  onMount(() => {
    measure();
    // Indoors a precise fix can take a minute. The geofence's rough one is enough to find the
    // cells and say which way they are; the precise one replaces it the moment it comes.
    const rough = alerts.position;
    if (rough !== undefined) position = { ...rough, accuracy: Number.NaN };
    const stopCompass = watchCompass((basis) => {
      if (basis === undefined) {
        if (compass === 'waiting') compass = 'missing';
        return;
      }
      latest = basis;
      if (compass !== 'live') compass = 'live';
    });

    // Precise, unlike the geofence's watch: the view is about tens of metres, and it runs only
    // while somebody is looking at it.
    const watch =
      navigator.geolocation === undefined
        ? undefined
        : navigator.geolocation.watchPosition(
            (fix) => {
              located = 'live';
              position = {
                lat: fix.coords.latitude,
                lon: fix.coords.longitude,
                accuracy: fix.coords.accuracy,
              };
            },
            (cause) => {
              if (cause.code === cause.PERMISSION_DENIED) located = 'refused';
            },
            { enableHighAccuracy: true, maximumAge: 2000, timeout: 20_000 },
          );

    void startCamera();
    void keepAwake();
    frame = requestAnimationFrame(draw);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void startCamera();
        void keepAwake();
      } else {
        stopCamera();
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onclose();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', measure);

    return () => {
      closed = true;
      stopCompass();
      if (watch !== undefined) navigator.geolocation.clearWatch(watch);
      stopCamera();
      void lock?.release().catch(() => undefined);
      if (frame !== undefined) cancelAnimationFrame(frame);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', measure);
    };
  });
</script>

<div class="viewfinder" role="dialog" aria-modal="true" aria-label="Umschau" bind:this={root}>
  <!-- The camera's own picture: no sound, nothing to caption, and it never leaves the page. -->
  <!-- svelte-ignore a11y_media_has_caption -->
  <video
    bind:this={video}
    class:live={camera === 'live'}
    muted
    playsinline
    autoplay
    onloadedmetadata={measure}
  ></video>
  <div class="shade"></div>
  {#if inside !== undefined}
    <div class="inside" style={`--colour: ${gdiColour(inside.gdi)}`}></div>
  {/if}
  <div class="scan" aria-hidden="true"></div>

  <div class="stage" class:off={compass !== 'live' || !portrait} aria-hidden="true">
    {#each shown as entry, rank (entry.cell)}
      {@const next = entry === nearest}
      <div
        class="target"
        class:near={next}
        class:far={entry.edge > RANGE_METRES}
        class:hot={entry.gdi >= 80}
        use:track={entry.cell}
        style={`--colour: ${gdiColour(entry.gdi)}; --rank: ${rank}; z-index: ${next ? MOST + 1 : MOST - rank}`}
      >
        <div class="ground"><i></i><i></i></div>
        <div class="beam"></div>
        <div class="tag">
          <div class="card">
            {#if next}<span class="badge">nächste</span>{/if}
            <span class="score"><strong>{entry.gdi}</strong> {vibeSymbol(entry.vibe)}</span>
            <span class="what">{gdiLabel(entry.gdi)}</span>
            <span class="far">{distance(entry.edge)}</span>
          </div>
        </div>
      </div>
    {/each}
  </div>

  <header class="top">
    <button class="close" onclick={onclose} aria-label="Umschau schließen">×</button>
    <span class="bearing" bind:this={heading}>{compass === 'missing' ? 'kein Kompass' : '…'}</span>
    <span class="fix">{fixText}</span>
  </header>

  <div class="tape" class:off={compass !== 'live'} aria-hidden="true">
    <div class="strip" bind:this={strip}>
      {#each TICKS as tick (tick)}
        <i class="tick" class:major={tick % 45 === 0} style={`left: ${(tick + 360) * TAPE_PX}px`}></i>
        {#if tick % 45 === 0}
          <span class="wind" style={`left: ${(tick + 360) * TAPE_PX}px`}>{compassPoint(tick)}</span>
        {/if}
      {/each}
      {#each shown as entry (entry.cell)}
        {#each [-360, 0, 360] as lap (lap)}
          <i
            class="dot"
            style={`left: ${(entry.bearing + lap + 360) * TAPE_PX}px; --colour: ${gdiColour(entry.gdi)}`}
          ></i>
        {/each}
      {/each}
    </div>
    <i class="caret"></i>
  </div>

  <span class="side left" bind:this={leftCount}></span>
  <span class="side right" bind:this={rightCount}></span>

  {#if nearest !== undefined && compass === 'live' && portrait}
    <div class="pointer" data-side={pointerSide} style={`--colour: ${gdiColour(nearest.gdi)}`}>
      <span class="chevron back">‹</span>
      <span>{distance(nearest.edge)}</span>
      <span class="chevron ahead">›</span>
    </div>
  {/if}

  <section class="sheet" aria-live="polite">
    {#if !portrait}
      <p class="note">Halte das Telefon hochkant.</p>
    {:else if located === 'refused'}
      <p class="note">Ohne Standortfreigabe weiß die Umschau nicht, wo du bist.</p>
    {:else if position === undefined}
      <p class="note searching">Suche deinen Standort …</p>
    {:else if !loaded}
      <p class="note searching">Suche Waben in der Nähe …</p>
    {:else if offline && focus === undefined}
      <p class="note">Ohne Netz gibt es keine Waben zu zeigen.</p>
    {:else if focus !== undefined}
      <div class="focus" style={`--colour: ${gdiColour(focus.gdi)}; --gdi: ${focus.gdi}%`}>
        <span class="dial"><strong>{focus.gdi}</strong><small>GDI</small></span>
        <div class="facts">
          <p class="kicker">{inside !== undefined ? 'Du bist mittendrin' : 'Nächste Wabe'}</p>
          <p class="title">{gdiLabel(focus.gdi)} {vibeSymbol(focus.vibe)}</p>
          <p class="line">
            {#if inside === undefined}
              <strong>{distance(focus.edge)}</strong> Richtung {compassPoint(focus.bearing)}
              {#if place !== undefined}· {place}{/if}
            {:else}
              {place ?? 'Diese Wabe'}
            {/if}
          </p>
          <p class="meta">{details}</p>
        </div>
        {#if inside === undefined && compass === 'live'}
          <span class="turn" bind:this={arrow} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="30" height="30">
              <path d="M12 3 L20 19 L12 15 L4 19 Z" fill="currentColor" />
            </svg>
          </span>
        {/if}
      </div>
      {#if compass === 'missing'}
        <p class="note">
          Dieses Telefon verrät seine Himmelsrichtung nicht, also stehen die Waben unten als
          Liste.
        </p>
        <ol class="list">
          {#each shown.slice(0, 4) as entry (entry.cell)}
            <li style={`--colour: ${gdiColour(entry.gdi)}`}>
              <strong>{entry.gdi}</strong>
              {gdiLabel(entry.gdi)} · {distance(entry.edge)} {compassPoint(entry.bearing)}
            </li>
          {/each}
        </ol>
      {/if}
    {:else}
      <p class="note">Hier in der Nähe ist gerade nichts los.</p>
    {/if}
    {#if camera === 'refused' || camera === 'missing'}
      <p class="fine">
        {camera === 'refused' ? 'Kamera nicht freigegeben' : 'Keine Kamera'} — die Richtungen
        stimmen trotzdem.
      </p>
    {:else}
      <p class="fine">Das Kamerabild bleibt auf deinem Telefon: nicht gesendet, nicht gespeichert.</p>
    {/if}
  </section>
</div>

<style>
  @property --fill {
    syntax: '<percentage>';
    inherits: false;
    initial-value: 0%;
  }

  .viewfinder {
    position: fixed;
    inset: 0;
    z-index: 30;
    overflow: hidden;
    background: radial-gradient(ellipse at 50% 40%, #1b2230, #05070a 75%);
    color: #fff;
    user-select: none;
    -webkit-user-select: none;
  }

  video {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0;
    transition: opacity 0.6s ease;
  }

  video.live {
    opacity: 1;
  }

  /* Darker at the edges and at top and bottom, where the text sits over whatever the camera
     happens to see. */
  .shade {
    position: absolute;
    inset: 0;
    pointer-events: none;
    background:
      radial-gradient(ellipse at 50% 45%, transparent 55%, rgb(0 0 0 / 40%) 100%),
      linear-gradient(to bottom, rgb(0 0 0 / 50%), transparent 22%, transparent 62%, rgb(0 0 0 / 60%));
  }

  /* Standing in a cell: its colour glows in from the edges, breathing. */
  .inside {
    position: absolute;
    inset: 0;
    pointer-events: none;
    box-shadow: inset 0 0 90px 24px var(--colour);
    animation: breathe 2.6s ease-in-out infinite;
  }

  /* One sweep down the screen as the view opens: looking round for cells. */
  .scan {
    position: absolute;
    left: 0;
    right: 0;
    top: -140px;
    height: 140px;
    pointer-events: none;
    background: linear-gradient(
      to bottom,
      transparent,
      rgb(255 255 255 / 8%) 60%,
      rgb(140 220 255 / 55%) 96%,
      transparent
    );
    animation: scan 1.5s cubic-bezier(0.4, 0, 0.6, 1) 0.15s 1 both;
  }

  .stage {
    position: absolute;
    inset: 0;
    pointer-events: none;
    transition: opacity 0.3s ease;
  }

  .stage.off {
    opacity: 0;
  }

  .target > * {
    position: absolute;
    left: 0;
    top: 0;
    transform-origin: 0 0;
    will-change: transform;
  }

  /* The column: light rising from the cell's middle, a glint running up it. */
  .beam {
    width: 6px;
    height: 100px;
    overflow: hidden;
    border-radius: 3px;
    background: linear-gradient(to top, transparent, var(--colour) 30%, #fff 100%);
    box-shadow:
      0 0 12px 2px var(--colour),
      0 0 30px 6px color-mix(in srgb, var(--colour) 35%, transparent);
  }

  .hot .beam {
    width: 10px;
  }

  /* The nearest cell beyond the range: a beacon low on the horizon, pulsing so it is found. */
  .far .beam {
    animation: beacon 1.6s ease-in-out infinite;
  }

  .beam::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 30%;
    background: linear-gradient(to top, transparent, rgb(255 255 255 / 90%), transparent);
    animation: glint 1.7s linear infinite;
    animation-delay: calc(var(--rank) * -0.23s);
  }

  /* The cell's foot: rings running out across the ground, the way the swarm pulse does. */
  .ground {
    width: 92px;
    height: 34px;
  }

  .ground i {
    position: absolute;
    inset: 0;
    border: 2px solid var(--colour);
    border-radius: 50%;
    box-shadow: 0 0 12px var(--colour);
    opacity: 0;
    animation: ripple 2.4s ease-out infinite;
    animation-delay: calc(var(--rank) * -0.31s);
  }

  .ground i + i {
    animation-delay: calc(var(--rank) * -0.31s - 1.2s);
  }

  .card {
    display: grid;
    grid-template-columns: auto auto;
    column-gap: 0.4rem;
    align-items: baseline;
    min-width: 4.6rem;
    padding: 0.35rem 0.6rem 0.4rem 0.55rem;
    border: 1px solid rgb(255 255 255 / 16%);
    border-left: 4px solid var(--colour);
    border-radius: 0.7rem;
    background: rgb(10 12 16 / 72%);
    box-shadow:
      0 6px 22px rgb(0 0 0 / 40%),
      0 0 22px -6px var(--colour);
    white-space: nowrap;
    animation: arrive 0.6s cubic-bezier(0.2, 1.5, 0.4, 1) both;
    animation-delay: calc(0.9s + var(--rank) * 0.07s);
  }

  .near .card {
    border-color: color-mix(in srgb, var(--colour) 70%, #fff);
    animation:
      arrive 0.6s cubic-bezier(0.2, 1.5, 0.4, 1) 0.9s both,
      beckon 1.8s ease-in-out 1.5s infinite;
  }

  .badge {
    grid-column: 1 / -1;
    justify-self: start;
    margin-bottom: 0.15rem;
    padding: 0 0.35rem;
    border-radius: 999px;
    background: var(--colour);
    color: #111;
    font-size: 0.625rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .score strong {
    font-size: 1.35rem;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
  }

  .what {
    grid-column: 1 / -1;
    font-size: 0.75rem;
    color: rgb(255 255 255 / 75%);
  }

  .far {
    font-size: 0.8125rem;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  .top {
    position: absolute;
    top: max(0.75rem, env(safe-area-inset-top));
    left: 0.75rem;
    right: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
  }

  .close {
    width: 2.6rem;
    height: 2.6rem;
    border: 1px solid rgb(255 255 255 / 25%);
    border-radius: 50%;
    background: rgb(10 12 16 / 55%);
    color: #fff;
    font-size: 1.6rem;
    line-height: 1;
    cursor: pointer;
  }

  .bearing {
    font-size: 1.05rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    font-variant-numeric: tabular-nums;
    text-shadow: 0 1px 4px rgb(0 0 0 / 60%);
  }

  .fix {
    min-width: 2.6rem;
    padding: 0.2rem 0.55rem;
    border-radius: 999px;
    background: rgb(10 12 16 / 55%);
    font-size: 0.75rem;
    text-align: center;
  }

  .tape {
    position: absolute;
    top: calc(max(0.75rem, env(safe-area-inset-top)) + 3.1rem);
    left: 0;
    right: 0;
    height: 2.4rem;
    overflow: hidden;
    -webkit-mask-image: linear-gradient(to right, transparent, #000 20%, #000 80%, transparent);
    mask-image: linear-gradient(to right, transparent, #000 20%, #000 80%, transparent);
    transition: opacity 0.3s ease;
  }

  /* Without a heading the tape would show some arbitrary stretch of the compass. */
  .tape.off {
    opacity: 0;
  }

  .strip {
    position: absolute;
    inset: 0 auto 0 0;
    will-change: transform;
  }

  .tick {
    position: absolute;
    bottom: 0;
    width: 1px;
    height: 7px;
    background: rgb(255 255 255 / 55%);
  }

  .tick.major {
    height: 13px;
    background: #fff;
  }

  .wind {
    position: absolute;
    top: 0.1rem;
    transform: translateX(-50%);
    font-size: 0.75rem;
    font-weight: 700;
    text-shadow: 0 1px 3px rgb(0 0 0 / 70%);
  }

  .dot {
    position: absolute;
    bottom: 16px;
    width: 7px;
    height: 7px;
    margin-left: -3.5px;
    border-radius: 50%;
    background: var(--colour);
    box-shadow: 0 0 8px var(--colour);
  }

  .caret {
    position: absolute;
    left: 50%;
    bottom: 0;
    width: 2px;
    height: 100%;
    margin-left: -1px;
    background: linear-gradient(to top, #fff, transparent);
  }

  .side {
    position: absolute;
    top: 46%;
    padding: 0.2rem 0.45rem;
    border-radius: 999px;
    background: rgb(10 12 16 / 55%);
    font-size: 0.8125rem;
    font-weight: 600;
  }

  .side:empty {
    display: none;
  }

  .side.left {
    left: 0.5rem;
  }

  .side.right {
    right: 0.5rem;
  }

  /* The nearest cell, while it is out of the picture: an arrow at the edge it is beyond. */
  .pointer {
    position: absolute;
    top: 38%;
    display: none;
    align-items: center;
    gap: 0.35rem;
    padding: 0.4rem 0.7rem;
    border: 2px solid var(--colour);
    border-radius: 999px;
    background: rgb(10 12 16 / 70%);
    box-shadow: 0 0 18px -2px var(--colour);
    font-weight: 700;
  }

  .pointer[data-side='left'] {
    display: flex;
    left: 0.6rem;
    animation: nudge-left 1.1s ease-in-out infinite;
  }

  .pointer[data-side='right'] {
    display: flex;
    right: 0.6rem;
    animation: nudge-right 1.1s ease-in-out infinite;
  }

  .pointer[data-side='left'] .ahead,
  .pointer[data-side='right'] .back {
    display: none;
  }

  .chevron {
    font-size: 1.4rem;
    line-height: 1;
    color: var(--colour);
  }

  .sheet {
    position: absolute;
    left: 0.75rem;
    right: 0.75rem;
    bottom: max(0.9rem, env(safe-area-inset-bottom));
    max-width: 28rem;
    margin-inline: auto;
    padding: 0.8rem 0.9rem 0.6rem;
    border: 1px solid rgb(255 255 255 / 14%);
    border-radius: 1.1rem;
    background: rgb(10 12 16 / 62%);
    -webkit-backdrop-filter: blur(10px);
    backdrop-filter: blur(10px);
    animation: rise 0.5s cubic-bezier(0.2, 1.2, 0.4, 1) 0.2s both;
  }

  .focus {
    display: flex;
    align-items: center;
    gap: 0.8rem;
  }

  /* The GDI as a dial that fills to its value when the card appears. */
  .dial {
    flex: none;
    display: grid;
    place-items: center;
    align-content: center;
    width: 3.6rem;
    height: 3.6rem;
    border-radius: 50%;
    background:
      radial-gradient(circle, rgb(10 12 16 / 92%) 58%, transparent 60%),
      conic-gradient(var(--colour) var(--fill), rgb(255 255 255 / 12%) 0);
    box-shadow: 0 0 22px -4px var(--colour);
    animation: fill 1.1s cubic-bezier(0.3, 0.9, 0.3, 1) 0.4s both;
  }

  .dial strong {
    font-size: 1.3rem;
    line-height: 1;
    font-variant-numeric: tabular-nums;
  }

  .dial small {
    font-size: 0.55rem;
    letter-spacing: 0.12em;
    opacity: 0.7;
  }

  .facts {
    flex: 1;
    min-width: 0;
  }

  .facts p {
    margin: 0;
  }

  .kicker {
    font-size: 0.6875rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--colour);
  }

  .title {
    font-size: 1.05rem;
    font-weight: 700;
  }

  .line {
    font-size: 0.875rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .meta {
    margin-top: 0.1rem !important;
    font-size: 0.75rem;
    color: rgb(255 255 255 / 70%);
  }

  .turn {
    flex: none;
    display: grid;
    place-items: center;
    width: 2.8rem;
    height: 2.8rem;
    border-radius: 50%;
    background: color-mix(in srgb, var(--colour) 22%, transparent);
    color: var(--colour);
    will-change: transform;
  }

  .note {
    margin: 0.2rem 0 0.35rem;
    font-size: 0.875rem;
  }

  .searching::after {
    content: '';
    display: inline-block;
    width: 0.6rem;
    height: 0.6rem;
    margin-left: 0.4rem;
    border: 2px solid currentcolor;
    border-right-color: transparent;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  .list {
    margin: 0 0 0.3rem;
    padding: 0;
    list-style: none;
    font-size: 0.8125rem;
  }

  .list li {
    padding: 0.15rem 0 0.15rem 0.5rem;
    border-left: 3px solid var(--colour);
  }

  .fine {
    margin: 0.45rem 0 0;
    font-size: 0.6875rem;
    color: rgb(255 255 255 / 55%);
  }

  @keyframes scan {
    to {
      transform: translateY(calc(100dvh + 140px));
    }
  }

  @keyframes glint {
    from {
      transform: translateY(0);
    }
    to {
      transform: translateY(-340%);
    }
  }

  @keyframes ripple {
    0% {
      transform: scale(0.25);
      opacity: 0.95;
    }
    100% {
      transform: scale(1.3);
      opacity: 0;
    }
  }

  @keyframes arrive {
    from {
      transform: translateY(-18px) scale(0.55);
      opacity: 0;
    }
  }

  @keyframes beckon {
    50% {
      box-shadow:
        0 6px 22px rgb(0 0 0 / 40%),
        0 0 30px 2px var(--colour);
    }
  }

  @keyframes breathe {
    50% {
      opacity: 0.45;
    }
  }

  @keyframes rise {
    from {
      transform: translateY(24px);
      opacity: 0;
    }
  }

  @keyframes fill {
    to {
      --fill: var(--gdi);
    }
  }

  @keyframes beacon {
    50% {
      opacity: 0.45;
    }
  }

  @keyframes nudge-left {
    50% {
      transform: translateX(-6px);
    }
  }

  @keyframes nudge-right {
    50% {
      transform: translateX(6px);
    }
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .scan,
    .beam::after,
    .far .beam,
    .ground i,
    .inside,
    .pointer {
      animation: none;
    }

    .card,
    .near .card,
    .sheet,
    .dial {
      animation-duration: 1ms;
      animation-iteration-count: 1;
    }

    .ground i {
      opacity: 0.8;
      transform: scale(0.8);
    }
  }
</style>
